/**
 * Comprehensive validation utilities for form validation and security
 */

// Email validation with comprehensive checks
export const validateEmail = (email) => {
  if (!email) return { isValid: false, message: 'Email is required' };
  
  // Basic format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }
  
  // Additional security checks
  if (email.length > 254) {
    return { isValid: false, message: 'Email address is too long' };
  }
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /\.{2,}/, // Multiple consecutive dots
    /@.*@/, // Multiple @ symbols
    /^\./, // Starting with dot
    /\.$/, // Ending with dot
    /\s/ // Contains spaces
  ];
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(email)) {
      return { isValid: false, message: 'Email format is invalid' };
    }
  }
  
  return { isValid: true, message: 'Valid email address' };
};

// Strong password validation
export const validatePassword = (password) => {
  if (!password) return { isValid: false, message: 'Password is required', strength: 0 };
  
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    noSpaces: !/\s/.test(password),
    noCommon: !isCommonPassword(password)
  };
  
  const score = Object.values(checks).filter(Boolean).length;
  const strength = Math.min(score, 5);
  
  const messages = [];
  if (!checks.length) messages.push('at least 8 characters');
  if (!checks.lowercase) messages.push('lowercase letter');
  if (!checks.uppercase) messages.push('uppercase letter');
  if (!checks.number) messages.push('number');
  if (!checks.special) messages.push('special character');
  if (!checks.noSpaces) messages.push('no spaces');
  if (!checks.noCommon) messages.push('not a common password');
  
  const isValid = score >= 4; // Require at least 4 out of 7 checks
  const message = isValid 
    ? 'Strong password' 
    : `Password needs: ${messages.join(', ')}`;
  
  return { isValid, message, strength, checks };
};

// Check for common passwords
const isCommonPassword = (password) => {
  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123',
    'password123', 'admin', 'letmein', 'welcome', 'monkey',
    '1234567890', 'password1', 'qwerty123', 'dragon', 'master'
  ];
  
  return commonPasswords.includes(password.toLowerCase());
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name) return { isValid: false, message: `${fieldName} is required` };
  
  if (name.trim().length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters long` };
  }
  
  if (name.length > 50) {
    return { isValid: false, message: `${fieldName} is too long` };
  }
  
  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(name)) {
    return { isValid: false, message: `${fieldName} contains invalid characters` };
  }
  
  return { isValid: true, message: 'Valid name' };
};

// Date of birth validation (server requires DOB and 18+ at signup)
export const validateDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) {
    return { isValid: false, message: 'Date of birth is required' };
  }

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) {
    return { isValid: false, message: 'Please enter a valid date of birth' };
  }

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  if (age < 18) {
    return { isValid: false, message: 'You must be at least 18 years old to join Hebrew Hearts' };
  }
  if (age > 120) {
    return { isValid: false, message: 'Please enter a valid date of birth' };
  }

  return { isValid: true, message: 'Valid date of birth' };
};

// Rate limiting validation
export const validateSubmissionRate = (lastSubmission, cooldownMs = 5000) => {
  if (!lastSubmission) return { isValid: true, message: 'Ready to submit' };
  
  const timeSinceLastSubmission = Date.now() - lastSubmission;
  const remainingCooldown = Math.max(0, cooldownMs - timeSinceLastSubmission);
  
  if (remainingCooldown > 0) {
    const secondsRemaining = Math.ceil(remainingCooldown / 1000);
    return { 
      isValid: false, 
      message: `Please wait ${secondsRemaining} seconds before submitting again`,
      remainingTime: remainingCooldown
    };
  }
  
  return { isValid: true, message: 'Ready to submit' };
};

// Form data sanitization
export const sanitizeFormData = (formData) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      // Remove potentially dangerous characters
      sanitized[key] = value
        .trim()
        .replace(/[<>]/g, '') // Remove < and > to prevent XSS
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+=/gi, ''); // Remove event handlers
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

// Comprehensive form validation
export const validateForm = (formData, options = {}) => {
  const errors = {};
  const warnings = {};
  
  // Validate email
  if (formData.email) {
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.message;
    }
  }
  
  // Validate password
  if (formData.password) {
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.message;
    }
    if (passwordValidation.strength < 3) {
      warnings.password = 'Consider using a stronger password';
    }
  }
  
  // Validate date of birth
  if (formData.dateOfBirth) {
    const dobValidation = validateDateOfBirth(formData.dateOfBirth);
    if (!dobValidation.isValid) {
      errors.dateOfBirth = dobValidation.message;
    }
  }

  // Validate names
  if (formData.firstName) {
    const firstNameValidation = validateName(formData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      errors.firstName = firstNameValidation.message;
    }
  }
  
  if (formData.lastName) {
    const lastNameValidation = validateName(formData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      errors.lastName = lastNameValidation.message;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings
  };
};

// Password strength indicator
export const getPasswordStrengthText = (strength) => {
  const strengthLevels = [
    'Very Weak',
    'Weak', 
    'Fair',
    'Good',
    'Strong',
    'Very Strong'
  ];
  
  return strengthLevels[strength] || 'Very Weak';
};

export const getPasswordStrengthColor = (strength) => {
  const colors = [
    '#dc2626', // Red - Very Weak
    '#ea580c', // Orange - Weak
    '#d97706', // Amber - Fair
    '#65a30d', // Lime - Good
    '#16a34a', // Green - Strong
    '#059669'  // Emerald - Very Strong
  ];
  
  return colors[strength] || colors[0];
};
